import {
  DatabaseAuto,
  IdataArrayResponse,
  IdeleteOneResponse,
  IecommerceCut,
  IfilterProps,
  TecommerceCutRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../../commerce-client';

/**
 * Represents an e-commerce platform's commission cut configuration in the system.
 * This class manages different commission tiers (lower, middle, upper) and custom cuts
 * that determine the platform's percentage take from sales.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TecommerceCut - The type definition for e-commerce cut properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Ecommerccut} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const ecommerccut = await Ecommerccut.getAll();
 *
 * const ecommerccut = await Ecommerccut.filterAll({
 *
 * });
 *
 * const ecommerccut = await Ecommerccut.bulkDelete({
 *
 * });
 *
 * const ecommerccut = await Ecommerccut.getOne('resourceId');
 *
 * const ecommerccut = await Ecommerccut.remove();
 *
 * ```
 */
export class Ecommerccut extends DatabaseAuto {
  /** Base API endpoint for e-commerce cut operations */
  private static readonly baseUrl = '/ecommerce-cut';

  /** Unique reference ID for the e-commerce cut configuration */
  readonly urId: IecommerceCut['urId'];

  /** Commission cut configuration for lower-tier sales */
  readonly lowerCut: IecommerceCut['lowerCut'];

  /** Commission cut configuration for middle-tier sales */
  readonly middleCut: IecommerceCut['middleCut'];

  /** Commission cut configuration for upper-tier sales */
  readonly upperCut: IecommerceCut['upperCut'];

  /** Array of custom commission cut configurations */
  readonly customCut: IecommerceCut['customCut'];

  /**
   * Creates a new instance of Ecommerccut.
   * @param {IecommerceCut} data - The e-commerce cut configuration data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: IecommerceCut) {
    /**
     * Validates that a commission cut object has valid amount and percentage values
     * @private
     * @param {any} v - The cut object to validate
     * @returns {boolean} True if the cut is valid
     */
    const validateCut = (v) => {
      if (!v.amount ||
        typeof v.amount !== 'number' ||
        v.amount < 0
      ) {
        return false;
      }

      if (!v.percentage ||
        typeof v.percentage !== 'number' ||
        v.percentage < 0
      ) {
        return false;
      }

      return true;
    };

    /**
     * Validates that a custom cut object has valid lower, upper, and percentage values
     * @private
     * @param {any} v - The custom cut object to validate
     * @returns {boolean} True if the custom cut is valid
     */
    const validateCustomCut = (v) => {
      if (!v.lowerAmount ||
        typeof v.lowerAmount !== 'number' ||
        v.lowerAmount < 0
      ) {
        return false;
      }

      if (!v.upperAmount ||
        typeof v.upperAmount !== 'number' ||
        v.upperAmount < 0
      ) {
        return false;
      }

      if (!v.percentage ||
        typeof v.percentage !== 'number' ||
        v.percentage < 0
      ) {
        return false;
      }

      return true;
    };

    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'urId must be a non-empty string'
      },
      lowerCut: {
        validate: validateCut,
        message: 'lowerCut must be a valid object with correct percentage cut see interface IpercCut'
      },
      middleCut: {
        validate: validateCut,
        message: 'middleCut must be a valid object with correct percentage cut see interface IpercCut'
      },
      upperCut: {
        validate: validateCut,
        message: 'upperCut must be a valid object with correct percentage cut see interface IpercCut'
      },
      customCut: {
        validate: (v) => {
          if (!Array.isArray(v)) {
            return false;
          }

          if (v.length === 0) {
            return true;
          }

          return v.every(el => validateCustomCut(el));
        },
        message: 'customCut must be a valid array with correct percentage cut see interface IpercCut[]'
      }
    });

    super(data);

    this.urId = data.urId;
    this.lowerCut = data.lowerCut;
    this.middleCut = data.middleCut;
    this.upperCut = data.upperCut;
    this.customCut = data.customCut;
  }

  /**
   * Retrieves all e-commerce cut configurations with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: Ecommerccut[] }>}
   * An object containing the total count and an array of e-commerce cut configurations
   * @throws {Error} If there's an error fetching the configurations
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<IecommerceCut>>(`${Ecommerccut.baseUrl}?${query}`);

    const ecommerceCuts = await lastValueFrom(observer$);

    return {
      count: ecommerceCuts.count,
      dataArr: ecommerceCuts.data.map((val) => new Ecommerccut(val))
    };
  }

  /**
   * Filters e-commerce cut configurations based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: Ecommerccut[] }>}
   * An object containing the total count and an array of filtered e-commerce cut configurations
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<IecommerceCut>>(
        `${Ecommerccut.baseUrl}/filter`,
        filter
      );

    const ecommerceCuts = await lastValueFrom(observer$);

    return {
      count: ecommerceCuts.count,
      dataArr: ecommerceCuts.data.map((val) => new Ecommerccut(val))
    };
  }

  /**
   * Retrieves a single e-commerce cut configuration by its ID.
   * @param {string} resourceId - The unique identifier of the configuration to retrieve
   * @returns {Promise<Ecommerccut>} The requested e-commerce cut configuration
   * @throws {Error} If the configuration is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<IecommerceCut>(`${Ecommerccut.baseUrl}/${resourceId}`);
    const ecommerceCut = await lastValueFrom(observer$);

    return new Ecommerccut(ecommerceCut);
  }

  /**
   * Creates a new e-commerce cut configuration.
   * @param {TecommerceCutRequest<true>} payload - The configuration data to create
   * @returns {Promise<Ecommerccut>} The newly created e-commerce cut configuration
   * @throws {Error} If the creation fails due to invalid data or server error
   */
  static async add(payload: TecommerceCutRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<IecommerceCut>(
        `${Ecommerccut.baseUrl}`,
        payload
      );

    return new Ecommerccut(await lastValueFrom(observer$));
  }

  /**
   * Updates the current e-commerce cut configuration with new values.
   * @param {TecommerceCutRequest} payload - The updated configuration data
   * @returns {Promise<Ecommerccut>} The updated e-commerce cut configuration
   * @throws {Error} If the update operation fails or the configuration doesn't exist
   */
  async update(payload: TecommerceCutRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<IecommerceCut>(
        `${Ecommerccut.baseUrl}/${this._id}`,
        payload
      );

    return new Ecommerccut(await lastValueFrom(observer$));
  }

  /**
   * Deletes the current e-commerce cut configuration.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion operation
   * @throws {Error} If the deletion fails or the configuration doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Ecommerccut.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
